// vmpa — frontend JS entry.
// main.css is enqueued separately by functions.php (avoids FOUC). Don't import it here.

// Two nav-markup generations are in play: the current Direction C header
// (`.nav-toggle` + `#nav-list`, opened by adding `.open` — see the r2
// `.nav-list.open{display:flex}` rule) and the older Tailwind header
// (`#mobile-menu`, opened by clearing the `hidden` attribute, which relies on
// preflight's `[hidden]{display:none !important}` beating the element's `flex`
// class). Support both so interior templates that still use the old part keep
// working; never mix the two mechanisms on one element.
function initNavToggle() {
  const toggle = document.querySelector('.nav-toggle');
  if (!toggle) return;

  const menu =
    (toggle.getAttribute('aria-controls') &&
      document.getElementById(toggle.getAttribute('aria-controls'))) ||
    document.getElementById('nav-list') ||
    document.getElementById('mobile-menu');
  if (!menu) return;

  const usesHidden = menu.id === 'mobile-menu';

  toggle.addEventListener('click', () => {
    const open = toggle.getAttribute('aria-expanded') === 'true';
    toggle.setAttribute('aria-expanded', String(!open));
    toggle.setAttribute('aria-label', open ? 'Open menu' : 'Close menu');
    if (usesHidden) {
      menu.hidden = open;
    } else {
      menu.classList.toggle('open', !open);
    }
  });
}

// State Street trolley strip: pick a fresh 13 businesses on every visit.
//
// The server (theme/inc/home-blocks.php, vmpa/state-street) already renders a
// valid random 13 so the strip works with JS off and for crawlers. This
// re-picks from the full pool the block emits as JSON, because site.v3.yaml
// enables full-page caching on live — a PHP-only shuffle would be frozen for
// the lifetime of each cached page and "a different 13 every visit" would
// quietly become "a different 13 per cache refresh".
//
// Nodes are built with createElement/textContent, never innerHTML: business
// names are client-editable content.
function initStateStops() {
  const list = document.getElementById('stops');
  const island = document.getElementById('state-street-pool');
  if (!list || !island) return;

  let pool;
  try {
    pool = JSON.parse(island.textContent || '[]');
  } catch (err) {
    return; // server-rendered 13 stays as-is
  }
  if (!Array.isArray(pool) || pool.length === 0) return;

  const count = Math.max(1, parseInt(list.dataset.count || '13', 10) || 13);
  const dir = list.dataset.dir || '/business-directory/';

  // Fisher-Yates, slice, THEN sort. The order matters: sorting the full pool
  // before slicing always yields the same geographically-first 13 and silently
  // cancels the shuffle. Re-sorting only the survivors keeps the strip reading
  // as a street — W numbers grow westward (descend), then E numbers ascend.
  const picks = pool.slice();
  for (let i = picks.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [picks[i], picks[j]] = [picks[j], picks[i]];
  }
  const chosen = picks.slice(0, count);
  chosen.sort((a, b) => {
    if (!!a.w !== !!b.w) return a.w ? -1 : 1;
    return a.w ? b.x - a.x : a.x - b.x;
  });

  const frag = document.createDocumentFragment();
  for (const stop of chosen) {
    const li = document.createElement('li');
    li.className = 'stop';

    const tick = document.createElement('span');
    tick.className = 'tick';
    tick.setAttribute('aria-hidden', 'true');

    const a = document.createElement('a');
    a.className = 'lab';
    a.href = dir + (stop.s ? '#business-' + encodeURIComponent(stop.s) : '');

    for (const [cls, text] of [
      ['addr', stop.a],
      ['name', stop.n],
      ['cat', stop.c],
    ]) {
      if (a.childNodes.length) a.appendChild(document.createTextNode(' '));
      const span = document.createElement('span');
      span.className = cls;
      span.textContent = String(text == null ? '' : text);
      a.appendChild(span);
    }

    li.append(tick, a);
    frag.appendChild(li);
  }

  list.replaceChildren(frag);
  list.style.setProperty('--n', String(chosen.length));
}

// "Just off State" row: a fresh few (data-count) of the off-street businesses
// on every visit. Mirrors initStateStops() (JSON pool → Fisher-Yates → rebuild) so the
// order is fresh even when the page itself is served from full-page cache.
function initStateOff() {
  const off = document.getElementById('offstate');
  const island = document.getElementById('state-street-off-pool');
  if (!off || !island) return;

  let pool;
  try {
    pool = JSON.parse(island.textContent || '[]');
  } catch (err) {
    return; // server-rendered order stays as-is
  }
  if (!Array.isArray(pool) || pool.length === 0) return;

  const dir = off.dataset.dir || '/business-directory/';
  const count = Math.max(1, parseInt(off.dataset.count || '4', 10) || 4);

  const picks = pool.slice();
  for (let i = picks.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [picks[i], picks[j]] = [picks[j], picks[i]];
  }

  const meta = off.querySelector('.meta');
  const frag = document.createDocumentFragment();
  if (meta) frag.appendChild(meta);

  for (const item of picks.slice(0, count)) {
    const a = document.createElement('a');
    a.href = dir + (item.s ? '#business-' + encodeURIComponent(item.s) : '');
    for (const [cls, text] of [
      ['addr', item.a],
      ['name', item.n],
      ['cat', item.c],
    ]) {
      if (a.childNodes.length) a.appendChild(document.createTextNode(' '));
      const span = document.createElement('span');
      span.className = cls;
      span.textContent = String(text == null ? '' : text);
      a.appendChild(span);
    }
    frag.appendChild(a);
  }

  off.replaceChildren(frag);
}

function initForms() {
  const forms = document.querySelectorAll('form.vmpa-form');
  forms.forEach((form) => {
    // Stamp the time-trap field with page-load epoch so the server can reject
    // submissions that arrive faster than a human could fill the form. Set
    // here (not in markup) so it reflects real presentation time; a no-JS
    // POST leaves ts=0 and the server skips the trap.
    const ts = form.querySelector('input[name="ts"]');
    if (ts) ts.value = String(Math.floor(Date.now() / 1000));

    form.addEventListener('submit', async (event) => {
      event.preventDefault();

      const button = form.querySelector('button[type="submit"]');
      const existingError = form.querySelector('.vmpa-form-error');
      if (existingError) existingError.remove();

      if (button) button.disabled = true;

      try {
        const response = await fetch('/wp-json/vmpa/v1/submit', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form),
        });
        const data = await response.json().catch(() => ({}));

        if (response.ok && data.ok) {
          const done = document.createElement('p');
          done.className = 'text-body';
          done.setAttribute('role', 'status');
          done.textContent = form.dataset.success || 'Thank you — we\'ll be in touch shortly.';
          form.replaceChildren(done);
          return;
        }

        showFormError(form, data.error || 'Something went wrong. Please try again.');
      } catch (err) {
        showFormError(form, 'Something went wrong. Please try again.');
      } finally {
        if (button) button.disabled = false;
      }
    });
  });
}

function showFormError(form, message) {
  const p = document.createElement('p');
  p.className = 'vmpa-form-error text-brick';
  p.textContent = message;
  form.appendChild(p);
}

// Public moderated event submission form (EV-4, archive `#submit` section).
// Separate from initForms()/vmpa-form above: this posts to the vmpa-events
// REST namespace (not vmpa/v1) and additionally carries a wp_rest nonce
// (localized via wp_localize_script as window.vmpaEventsSubmit — see
// MPB\VmpaEvents\Submission::enqueue()). Same honeypot + time-trap shape as
// the other forms; the nonce is on top, not instead of.
function initEventSubmit() {
  const form = document.querySelector('form[data-vmpa-events-submit]');
  if (!form) return;

  const ts = form.querySelector('input[name="_ts"]');
  if (ts) ts.value = String(Math.floor(Date.now() / 1000));

  const nonceField = form.querySelector('input[name="submit_nonce"]');
  if (nonceField && window.vmpaEventsSubmit && window.vmpaEventsSubmit.nonce) {
    nonceField.value = window.vmpaEventsSubmit.nonce;
  }

  const endpoint =
    (window.vmpaEventsSubmit && window.vmpaEventsSubmit.endpoint) || form.getAttribute('action');

  form.addEventListener('submit', async (event) => {
    event.preventDefault();

    const button = form.querySelector('button[type="submit"]');
    const existingMessage = form.querySelector('.vmpa-events-submit-message');
    if (existingMessage) existingMessage.remove();

    if (button) button.disabled = true;

    try {
      // Logged-in visitors: REST treats a cookie without this header as logged
      // out, and the body nonce (made for the logged-in user) then fails.
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-WP-Nonce': (window.vmpaEventsSubmit && window.vmpaEventsSubmit.nonce) || '' },
        body: new FormData(form),
      });
      const data = await response.json().catch(() => ({}));

      if (response.ok && data.ok) {
        form.innerHTML =
          '<p class="vmpa-events-submit-message" role="status">Thanks — your event has been submitted for review and will appear on the calendar once approved.</p>';
        return;
      }

      showEventSubmitError(form, data.error || 'Something went wrong. Please try again.');
    } catch (err) {
      showEventSubmitError(form, 'Something went wrong. Please try again.');
    } finally {
      if (button) button.disabled = false;
    }
  });
}

function showEventSubmitError(form, message) {
  const p = document.createElement('p');
  p.className = 'vmpa-events-submit-message text-brick';
  p.textContent = message;
  form.appendChild(p);
}

// Events pop-up notification (Figma 395:1002). Markup is server-rendered
// hidden-by-default (only present at all when a today-matching `_notices`
// entry exists — see MPB\VmpaEvents\SingleRender::render_notice_popup()), so
// there's no fetch here: just show-if-not-dismissed + focus + Esc + dismiss.
// Dismissal key includes the notice date + event id so a NEW notice (new
// date, or a different event) always re-shows even if a past one was closed.
function initEventsPopup() {
  const popup = document.querySelector('[data-vmpa-events-popup]');
  if (!popup) return;

  const dismissKey = popup.getAttribute('data-dismiss-key');
  if (!dismissKey) return;

  let dismissed = false;
  try {
    dismissed = window.localStorage.getItem(dismissKey) === '1';
  } catch (err) {
    dismissed = false;
  }
  if (dismissed) return;

  const closeButton = popup.querySelector('[data-vmpa-events-popup-close]');
  const previouslyFocused = document.activeElement;
  const focusableSelector =
    'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function dismiss() {
    popup.hidden = true;
    document.removeEventListener('keydown', onKeydown);
    try {
      window.localStorage.setItem(dismissKey, '1');
    } catch (err) {
      // localStorage unavailable (private mode / quota) — popup still closes
      // for this view, it just may re-show on reload.
    }
    if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
      previouslyFocused.focus();
    }
  }

  // Trap Tab focus inside the dialog so it can't escape to the page behind
  // while the modal overlay is open (aria-modal alone doesn't enforce this).
  function trapTab(event) {
    const focusable = Array.from(popup.querySelectorAll(focusableSelector)).filter(
      (el) => el.offsetParent !== null,
    );
    if (focusable.length === 0) {
      event.preventDefault();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey) {
      if (document.activeElement === first || !popup.contains(document.activeElement)) {
        event.preventDefault();
        last.focus();
      }
    } else if (document.activeElement === last || !popup.contains(document.activeElement)) {
      event.preventDefault();
      first.focus();
    }
  }

  function onKeydown(event) {
    if (event.key === 'Escape') {
      dismiss();
    } else if (event.key === 'Tab') {
      trapTab(event);
    }
  }

  if (closeButton) {
    closeButton.addEventListener('click', dismiss);
  }
  document.addEventListener('keydown', onKeydown);

  popup.hidden = false;
  if (closeButton) closeButton.focus();
}

// Public moderated business submission form (BD-3, directory archive
// `#submit` section). Same shape as initEventSubmit() above: honeypot +
// time-trap fields live in the markup already; this just stamps _ts,
// localizes the nonce (window.vmpaDirectorySubmit — see
// MPB\VmpaDirectory\Submission::enqueue()), and posts via fetch with an
// inline success/error message (textContent only, never innerHTML from
// user/server strings).
function initBusinessSubmit() {
  const form = document.querySelector('form[data-vmpa-directory-submit]');
  if (!form) return;

  const ts = form.querySelector('input[name="_ts"]');
  if (ts) ts.value = String(Math.floor(Date.now() / 1000));

  const nonceField = form.querySelector('input[name="submit_nonce"]');
  if (nonceField && window.vmpaDirectorySubmit && window.vmpaDirectorySubmit.nonce) {
    nonceField.value = window.vmpaDirectorySubmit.nonce;
  }

  const endpoint =
    (window.vmpaDirectorySubmit && window.vmpaDirectorySubmit.endpoint) || form.getAttribute('action');

  form.addEventListener('submit', async (event) => {
    event.preventDefault();

    const button = form.querySelector('button[type="submit"]');
    const existingMessage = form.querySelector('.vmpa-directory-submit-message');
    if (existingMessage) existingMessage.remove();

    if (button) button.disabled = true;

    try {
      // Logged-in visitors: REST treats a cookie without this header as logged
      // out, and the body nonce (made for the logged-in user) then fails.
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-WP-Nonce': (window.vmpaDirectorySubmit && window.vmpaDirectorySubmit.nonce) || '' },
        body: new FormData(form),
      });
      const data = await response.json().catch(() => ({}));

      if (response.ok && data.ok) {
        form.innerHTML =
          '<p class="vmpa-directory-submit-message" role="status">Thanks — your business has been submitted for review and will appear in the directory once approved.</p>';
        return;
      }

      showBusinessSubmitError(form, data.error || 'Something went wrong. Please try again.');
    } catch (err) {
      showBusinessSubmitError(form, 'Something went wrong. Please try again.');
    } finally {
      if (button) button.disabled = false;
    }
  });
}

function showBusinessSubmitError(form, message) {
  const p = document.createElement('p');
  p.className = 'vmpa-directory-submit-message text-brick';
  p.textContent = message;
  form.appendChild(p);
}

// Archive filters without a page reload: business directory tabs + search,
// events calendar month arrows / view switch / search / category, and store
// categories. Every control is a plain link or GET form, so without JS they
// still work by reloading. With JS, fetch the same URL, swap in the new
// archive root, push the URL so Back/Forward and shared links behave, and
// keep the controls in view instead of jumping back to the top of the page.
// Any failure falls back to a normal navigation.
const IN_PLACE_ARCHIVES = [
  {
    key: 'directory',
    root: '.vmpa-directory-archive',
    links: 'a.vmpa-directory-tab',
    forms: 'form.vmpa-directory-search',
    controls: '.vmpa-directory-controls',
    announce(root) {
      const rows = root.querySelectorAll('.vmpa-directory-row').length;
      const active = root.querySelector('.vmpa-directory-tab.is-active');
      const label = active ? active.textContent.trim() : 'All';
      return rows
        ? `Showing ${rows} ${rows === 1 ? 'business' : 'businesses'}${label && label !== 'All' ? ` in ${label}` : ''}.`
        : 'No businesses match.';
    },
  },
  {
    key: 'events',
    root: '.vmpa-events-archive',
    links: 'a.vmpa-events-month-arrow, a.vmpa-events-view-link',
    forms: 'form.vmpa-events-search, form.vmpa-events-cat-form',
    // The category <select> submits itself (inline onchange → form.submit(),
    // which fires no submit event); route its change through the swap instead.
    autoSubmit: 'select.vmpa-events-cat-select',
    controls: '.vmpa-events-controls',
    announce(root) {
      const month = root.querySelector('.vmpa-events-month-label');
      return month ? `Showing events for ${month.textContent.trim()}.` : 'Events updated.';
    },
  },
  {
    key: 'store',
    root: '.r2-store',
    links: '.wc-block-product-categories-list a',
    controls: null,
    current: '.wc-block-product-categories-list a',
    announce(root) {
      const n = root.querySelectorAll('.r2-store__grid li.wc-block-product, .r2-store__grid .wc-block-product').length;
      const active = root.querySelector('.wc-block-product-categories-list a[aria-current]');
      return `Showing ${n} ${n === 1 ? 'product' : 'products'}${active ? ` in ${active.textContent.trim()}` : ''}.`;
    },
  },
];

function initInPlaceArchive(cfg) {
  if (!document.querySelector(cfg.root) || !window.fetch || !window.DOMParser) return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const stateKey = `vmpa-${cfg.key}`;
  let controller = null;

  const live = document.createElement('p');
  live.className = 'screen-reader-text';
  live.setAttribute('aria-live', 'polite');
  document.querySelector(cfg.root).after(live);

  // Mark the link for the current URL (store categories carry no active class).
  function markCurrent(root) {
    if (!cfg.current) return;
    const here = window.location.href.split(/[?#]/)[0];
    root.querySelectorAll(cfg.current).forEach((a) => {
      if (a.href.split(/[?#]/)[0] === here) a.setAttribute('aria-current', 'page');
      else a.removeAttribute('aria-current');
    });
  }

  function keepInView(root) {
    // Bring the top of the archive (its controls) to the top of the screen,
    // under the header when it is sticky, so the new results are what the
    // visitor sees. Measured on the archive itself, never on the controls:
    // they are position:sticky, so once stuck they report top 0 wherever the
    // page is, and the old measurement left the visitor deep in the previous
    // month's list (worst on phones, where the lists are long).
    const head = document.querySelector('.site-head');
    const sticky = head && ['sticky', 'fixed'].includes(getComputedStyle(head).position);
    const offset = (sticky ? head.offsetHeight : 0) + 16;
    const top = Math.max(0, root.getBoundingClientRect().top + window.scrollY - offset);
    if (window.scrollY > top + 8) {
      // Only ever scroll UP to the archive: a visitor above it (still reading
      // the hero) keeps their place.
      window.scrollTo({ top, behavior: reduceMotion ? 'auto' : 'smooth' });
    }
  }

  async function load(url, { push = true, focusHref = null } = {}) {
    const root = document.querySelector(cfg.root);
    if (!root) return void (window.location.href = url);
    if (controller) controller.abort();
    controller = new AbortController();
    root.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(url, {
        signal: controller.signal,
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
      });
      if (!response.ok) throw new Error(String(response.status));
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const next = doc.querySelector(cfg.root);
      if (!next) throw new Error('no archive in response');

      root.replaceWith(next);
      if (push) history.pushState({ [stateKey]: true }, '', response.url || url);
      if (doc.title) document.title = doc.title;
      markCurrent(next);
      live.textContent = cfg.announce(next);
      keepInView(next);
      if (focusHref) {
        const link = [...next.querySelectorAll(cfg.links)].find((a) => a.href === focusHref);
        if (link) link.focus({ preventScroll: true });
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      window.location.href = url;
    }
  }

  function submit(form) {
    const url = new URL(form.action, window.location.href);
    url.search = '';
    new FormData(form).forEach((value, key) => {
      if (String(value).trim() !== '') url.searchParams.set(key, String(value));
    });
    load(url.toString());
  }

  document.addEventListener('click', (event) => {
    const link = event.target.closest(`${cfg.root} :is(${cfg.links})`);
    if (!link || event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return; // new tab/window
    event.preventDefault();
    if (link.classList.contains('is-active') || link.getAttribute('aria-current') === 'page') return;
    load(link.href, { focusHref: link.href });
  });

  if (cfg.forms) {
    document.addEventListener('submit', (event) => {
      const form = event.target.closest(`${cfg.root} :is(${cfg.forms})`);
      if (!form) return;
      event.preventDefault();
      submit(form);
    });
  }

  if (cfg.autoSubmit) {
    // Capture phase on document runs before the element's inline onchange;
    // stopping propagation here keeps that handler from reloading the page.
    document.addEventListener('change', (event) => {
      const field = event.target.closest(`${cfg.root} :is(${cfg.autoSubmit})`);
      if (!field || !field.form) return;
      event.stopPropagation();
      submit(field.form);
    }, true);
  }

  markCurrent(document.querySelector(cfg.root));

  // Back/Forward between filter states re-renders without a reload.
  history.replaceState({ ...(history.state || {}), [stateKey]: true }, '', window.location.href);
  window.addEventListener('popstate', (event) => {
    if (event.state && event.state[stateKey]) load(window.location.href, { push: false });
  });
}

function initInPlaceArchives() {
  IN_PLACE_ARCHIVES.forEach(initInPlaceArchive);
}

// Places to Stay: Visit Delco's full list is 40+ cards; show the nearest 12
// with a button for the rest. Without JS every card shows.
function initStaysMore() {
  const list = document.querySelector('[data-stays]');
  const button = document.querySelector('[data-stays-more]');
  if (!list || !button) return;
  list.classList.add('is-collapsed');
  button.parentElement.hidden = false;
  button.addEventListener('click', () => {
    list.classList.remove('is-collapsed');
    button.parentElement.remove();
    const next = list.children[12] && list.children[12].querySelector('a');
    if (next) next.focus();
  });
}

// Homepage hero video (render_hero): the <video> ships with preload="none"
// and its <source> URLs in data-src, so the multi-MB clip never competes with
// the hero photo (the LCP element) or the stylesheets for bandwidth. Once the
// page has loaded and the main thread is idle, swap the URLs in and start
// playback; the photo underneath stays visible until the first frame paints.
// Skipped for reduced-motion and Save-Data visitors — they keep the photo.
function initDeferredVideo() {
  const videos = document.querySelectorAll('video[data-deferred-video]');
  if (!videos.length) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (navigator.connection && navigator.connection.saveData) return;

  videos.forEach((video) => {
    video.querySelectorAll('source[data-src]').forEach((source) => {
      source.src = source.dataset.src;
      source.removeAttribute('data-src');
    });
    video.preload = 'auto';
    video.autoplay = true;
    video.load();
    const play = video.play();
    if (play && typeof play.catch === 'function') play.catch(() => {});
  });
}

// Started a few seconds after load, not at load: a clip requested before the
// hero photo has painted is counted against that paint (Lighthouse's
// simulated phone budget put the 2.5 MB clip ahead of the photo and scored
// LCP at 4 s). The photo is the hero until then, so nothing visibly waits.
const VIDEO_DELAY_MS = 3000;

function scheduleDeferredVideo() {
  const run = () =>
    window.setTimeout(
      () =>
        'requestIdleCallback' in window
          ? window.requestIdleCallback(initDeferredVideo, { timeout: 2000 })
          : initDeferredVideo(),
      VIDEO_DELAY_MS
    );
  if (document.readyState === 'complete') run();
  else window.addEventListener('load', run, { once: true });
}

// Business Directory deep links (#business-<slug> from the homepage strip, the
// map card or "Show on map"): the search/tab bar is sticky, so publish its
// height for the rows' scroll-margin and re-run the jump once it's known.
function initDirectoryAnchor() {
  const bar = document.querySelector('.vmpa-directory-controls');
  if (!bar) return;
  const measure = () => document.documentElement.style.setProperty('--vmpa-dir-sticky', bar.offsetHeight + 'px');
  const jump = () => {
    if (!/^#business-[\w-]+$/.test(location.hash)) return;
    const row = document.getElementById(decodeURIComponent(location.hash.slice(1)));
    if (row) row.scrollIntoView({ block: 'start' });
  };
  measure();
  jump();
  window.addEventListener('load', () => { measure(); jump(); }, { once: true });
  window.addEventListener('resize', measure);
  window.addEventListener('hashchange', jump);
}

scheduleDeferredVideo();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initNavToggle();
  initStateStops();
    initStateOff();
    initForms();
    initEventSubmit();
    initEventsPopup();
    initBusinessSubmit();
    initInPlaceArchives();
    initStaysMore();
    initDirectoryAnchor();
  });
} else {
  initNavToggle();
  initStateStops();
  initStateOff();
  initForms();
  initEventSubmit();
  initEventsPopup();
  initBusinessSubmit();
  initInPlaceArchives();
  initStaysMore();
  initDirectoryAnchor();
}
